import { useCallback, useEffect, useState } from 'react';
import Icon from '../components/Icon';
import { apiService } from '@/services/api';

/**
 * Notification settings (wording and structure follow MagniInbox's ConfigurationsNotifications
 * page). One choice for now: product updates by email, stored per user in the shared
 * Notification service (opt-in, off until turned on). The switch saves as soon as it changes;
 * if the save fails it flips back and says so. Product-update emails link here, so this is
 * also where people turn them off.
 */
export const NotificationSettingsPage = () => {
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await apiService.getProductUpdatePreferences();
      setEmailEnabled(res.value?.emailEnabled ?? false);
      setAvailable(res.value?.available ?? false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setLoadError(`Failed to load notification preferences: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleEmail = async () => {
    if (!available || isSaving) return;
    const previous = emailEnabled;
    const next = !previous;
    setEmailEnabled(next);
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await apiService.setProductUpdatePreferences(next);
      setEmailEnabled(res.value?.emailEnabled ?? next);
      setSuccessMessage('Preferences saved.');
    } catch (err) {
      setEmailEnabled(previous);
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(`Failed to save preferences: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-sm text-slate-500 dark:text-slate-400">Loading notification preferences...</div>
    );
  }

  if (loadError) {
    return (
      <div className="p-8 max-w-3xl">
        <div className="mb-3 text-sm text-red-700 dark:text-red-400">{loadError}</div>
        <button
          type="button"
          onClick={() => void load()}
          className="px-4 py-2 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          Retry
        </button>
      </div>
    );
  }

  const disabled = !available || isSaving;

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-1">Notification settings</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        Choose how you want to hear about what's new in Open Banking.
      </p>

      {!available && (
        <div className="mb-4 p-3 text-sm rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
          Notification settings are temporarily unavailable. Please try again later.
        </div>
      )}

      {error && (
        <div role="alert" className="mb-4 p-3 text-sm rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {successMessage && (
        <div role="status" className="mb-4 p-3 text-sm rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
          {successMessage}
        </div>
      )}

      <section className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <Icon name="notifications" size={22} className="text-primary" />
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Product updates</h2>
        </div>

        <div className="flex items-start justify-between gap-6 px-6 py-5">
          <div>
            <p id="email-product-updates-label" className="text-sm font-medium text-slate-800 dark:text-slate-200">
              Email me product updates
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Updates are sent to your account email, usually as a weekly summary, and only for news published after you turn this on.
              They always appear in the notification bell.
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={emailEnabled}
            aria-labelledby="email-product-updates-label"
            disabled={disabled}
            onClick={() => void toggleEmail()}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 ${
              emailEnabled ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                emailEnabled ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </section>
    </div>
  );
};
